import {
  ControlOutlined,
  ExportOutlined,
  EyeOutlined,
  FlagOutlined,
  PlaySquareOutlined,
  PrinterOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { MenuProps } from 'antd'
import { t } from 'i18next'
import { useCallback, useMemo } from 'react'
import {
  algorithmRunStatuses,
  ongoingStatuses,
  riskReliefSmartFunctionTypes,
  workspaceTypes,
} from '../constants/constants'
import {
  BiomarkConfigurationModal,
  BiomarkLastConfigurationModal,
} from '../pages/SmartFunctions/BiomarkSmartFunctionDetailsPage/modals'
import CompensationMatrixAnalysisConfigurationModal from '../pages/SmartFunctions/CompensationMatrixAnalysisSmartFunctionDetailsPage/Modals/CompensationMatrixAnalysisConfigurationModal/CompensationMatrixAnalysisConfigurationModal'
import CompensationMatrixAnalysisLastConfigurationModal from '../pages/SmartFunctions/CompensationMatrixAnalysisSmartFunctionDetailsPage/Modals/CompensationMatrixAnalysisLastConfigurationModal/CompensationMatrixAnalysisLastConfigurationModal'
import { FlagDeviationsModal } from '../pages/SmartFunctions/ConformityCheckQualityReviewSmartFunctionDetailsPage/modals'
import {
  DelConfigurationModal,
  DelLastConfigurationModal,
} from '../pages/SmartFunctions/DelSmartFunctionDetailsPage/modals'
import FlowCytometryReviewAnalysisConfigurationModal from '../pages/SmartFunctions/FlowCytometryReviewAnalysisSmartFunctionDetailsPage/modals/FlowCytometryReviewAnalysisConfigurationModal/FlowCytometryReviewAnalysisConfigurationModal'
import {
  RegulatorySubmissionConfigurationModal,
  RegulatorySubmissionLastConfigurationModal,
} from '../pages/SmartFunctions/RegulatorySubmissionFunctionDetailsPage/modals'
import { ExtendedAlgorithmRun } from '../pages/WorkspaceDetailsPage/panels/WorkspaceSmartFunctionPanel'
import { smartFunctionActionService } from '../services'
import {
  AlgorithmRun,
  ConformityCheckQualityReviewItem,
  RiskReliefAlgorithmRun,
} from '../store/reducers/workspaceReducer'
import useSmartFunctionActions, {
  SmartFunctionActionDropdownKeys,
} from './useSmartFunctionActions'
import useWorkspaceDetails from './useWorkspaceDetails'
import useWorkspaceSmartFunctionData from './useWorkspaceSmartFunctionData'

interface UseSmartFunctionActionDropdownItemsProps {
  workspaceId?: string
  algorithmRunId?: string
  algorithmRun?: AlgorithmRun | RiskReliefAlgorithmRun
  showFlagDeviationsMenuItem?: boolean
  deviations?: ConformityCheckQualityReviewItem[]
}

const useSmartFunctionActionDropdownItems = ({
  workspaceId,
  algorithmRunId,
  algorithmRun,
  showFlagDeviationsMenuItem,
  deviations,
}: UseSmartFunctionActionDropdownItemsProps) => {

  const { calculateStatusLabQr } = useWorkspaceSmartFunctionData()
  const { selectedWorkspace } = useWorkspaceDetails({
    workspaceId,
    preventFetch: true,
  })
  const { isRiskReliefRunAvailable, isOfrQrRunAvailable } =
    useSmartFunctionActions({
      workspaceId,
      algorithmRun,
      algorithmRunId,
    })

  const isRiskReleifRunCancellable = (algorithmRun: RiskReliefAlgorithmRun) => {
    // handle edge case where run is paused and root cause discovery is ongoing
    return (
      algorithmRun?.runStatus === algorithmRunStatuses.PAUSED &&
      (algorithmRun as RiskReliefAlgorithmRun)?.nodeStatuses
        ?.ROOT_CAUSE_DISCOVERY === algorithmRunStatuses.ONGOING
    )
  }

  const isOfrQrRunCancellable = useCallback(
    (run: ExtendedAlgorithmRun) => {
      const status =
        run?.calculatedStatus ??
        calculateStatusLabQr({
          status: run?.nodeStatuses?.LABQR,
          runs: [run],
        })
      return ongoingStatuses.includes(status!)
    },
    [calculateStatusLabQr]
  )

  const isCancellable = useMemo(() => {
    if (!algorithmRun) return false
    if (
      (algorithmRun as RiskReliefAlgorithmRun)?.smartFunctionType ===
      riskReliefSmartFunctionTypes.ROOT_CAUSE_DISCOVERY
    ) {
      return isRiskReleifRunCancellable(algorithmRun as RiskReliefAlgorithmRun)
    }
    if (selectedWorkspace?.typeCode === workspaceTypes.OFR_QR) {
      return isOfrQrRunCancellable(algorithmRun as ExtendedAlgorithmRun)
    }
    return (
      algorithmRun?.runStatus === algorithmRunStatuses.ONGOING ||
      algorithmRun?.runStatus === algorithmRunStatuses.ONGOING_AI ||
      algorithmRun?.runStatus === algorithmRunStatuses.ONGOING_INGESTION
    )
  }, [algorithmRun, selectedWorkspace, isOfrQrRunCancellable])

  const isResumeRun = useMemo(
    () =>
      (algorithmRun as RiskReliefAlgorithmRun)?.smartFunctionType ===
      riskReliefSmartFunctionTypes.CAPA_GENERATION,
    [algorithmRun]
  )

  const isRunsAvailable = useMemo(() => {
    if (!algorithmRun) return false

    if (selectedWorkspace?.typeCode === workspaceTypes.RISK_REL) {
      return isRiskReliefRunAvailable(algorithmRun)
    }
    if (selectedWorkspace?.typeCode === workspaceTypes.OFR_QR) {
      return isOfrQrRunAvailable(algorithmRun as ExtendedAlgorithmRun)
    }

    return (
      !algorithmRun?.runStatus ||
      algorithmRun.runStatus === algorithmRunStatuses.COMPLETE ||
      algorithmRun.runStatus === algorithmRunStatuses.FAILED ||
      algorithmRun.runStatus === algorithmRunStatuses.READY ||
      algorithmRun.runStatus === algorithmRunStatuses.CANCELLED ||
      algorithmRun.runStatus === algorithmRunStatuses.PAUSED
    )
  }, [
    algorithmRun,
    isRiskReliefRunAvailable,
    isOfrQrRunAvailable,
    selectedWorkspace,
  ])

  const getCommonMenuItems = useCallback(
    (): MenuProps['items'] => [
      {
        key: SmartFunctionActionDropdownKeys.RUN_ALGORITHM,
        icon: <PlaySquareOutlined />,
        disabled: !isRunsAvailable,
        label: isResumeRun
          ? t(
            'workspaceDetailsPage.workspaceSmartFunctionPanel.actionMenu.resumeRun'
          )
          : t(
            'workspaceDetailsPage.workspaceSmartFunctionPanel.actionMenu.runAlgorithm'
          ),
      },
      {
        key: SmartFunctionActionDropdownKeys.CANCEL_RUN,
        icon: <StopOutlined />,
        disabled: !isCancellable,
        label: t(
          'workspaceDetailsPage.workspaceSmartFunctionPanel.actionMenu.cancelRun'
        ),
      },
      ...(algorithmRunId
        ? [
          {
            key: SmartFunctionActionDropdownKeys.PRINT_CONTENT,
            disabled:
              algorithmRun?.runStatus !== algorithmRunStatuses.COMPLETE,
            icon: <PrinterOutlined />,
            label: t(
              'workspaceDetailsPage.workspaceSmartFunctionPanel.actionMenu.printContent'
            ),
          },
        ]
        : []),
      ...(selectedWorkspace?.typeCode !== workspaceTypes.REGSUB &&
        selectedWorkspace?.typeCode !== workspaceTypes.DEL
        ? [
          {
            key: SmartFunctionActionDropdownKeys.EXPORT_RUN,
            disabled:
              algorithmRun?.runStatus !== algorithmRunStatuses.COMPLETE,
            icon: <ExportOutlined />,
            label: t(
              'workspaceDetailsPage.workspaceSmartFunctionPanel.actionMenu.exportRun'
            ),
          },
        ]
        : []),
    ],
    [
      algorithmRun,
      algorithmRunId,
      isCancellable,
      selectedWorkspace,
      isResumeRun,
      isRunsAvailable,
    ]
  )

  const getWorkspaceTypeItems = useCallback((): MenuProps['items'] => {
    if (!algorithmRun || !selectedWorkspace) return []

    switch (selectedWorkspace?.typeCode) {
      case workspaceTypes.BIOMARK: {
        const config = smartFunctionActionService.getBiomarkConfig(
          algorithmRun,
          selectedWorkspace
        )
        return [
          {
            key: SmartFunctionActionDropdownKeys.BIOMARK_CONFIGURATION,
            icon: <ControlOutlined />,
            label: <BiomarkConfigurationModal config={config} />,
          },
          {
            key: SmartFunctionActionDropdownKeys.VIEW_BIOMARK_RUN_SETUP,
            icon: <EyeOutlined />,
            label: <BiomarkLastConfigurationModal config={config} />,
          },
        ]
      }
      case workspaceTypes.REGSUB: {
        const config = smartFunctionActionService.getRegsubConfig(algorithmRun)
        return [
          {
            key: SmartFunctionActionDropdownKeys.REGSUB_CONFIGURATION,
            icon: <ControlOutlined />,
            label: <RegulatorySubmissionConfigurationModal config={config} />,
          },
          {
            key: SmartFunctionActionDropdownKeys.VIEW_REGSUB_RUN_SETUP,
            icon: <EyeOutlined />,
            label: (
              <RegulatorySubmissionLastConfigurationModal config={config} />
            ),
          },
        ]
      }
      case workspaceTypes.DEL: {
        const config = smartFunctionActionService.getDelConfig(algorithmRun)
        return [
          {
            key: SmartFunctionActionDropdownKeys.DEL_CONFIGURATION,
            icon: <ControlOutlined />,
            label: <DelConfigurationModal config={config} />,
          },
          {
            key: SmartFunctionActionDropdownKeys.VIEW_DEL_RUN_SETUP,
            icon: <EyeOutlined />,
            label: <DelLastConfigurationModal config={config} />,
          },
        ]
      }
      case workspaceTypes.OFR_QR: {
        return showFlagDeviationsMenuItem
          ? [
            {
              key: SmartFunctionActionDropdownKeys.OFR_QR,
              icon: <FlagOutlined />,
              label: <FlagDeviationsModal deviations={deviations} />,
            },
          ]
          : []
      }
      case workspaceTypes.CC_FC: {
        return [
          {
            key: SmartFunctionActionDropdownKeys.COMPENSATION_MATRIX_CONFIGURATION,
            icon: <ControlOutlined />,
            label: <CompensationMatrixAnalysisConfigurationModal />,
          },
          {
            key: SmartFunctionActionDropdownKeys.COMPENSATION_MATRIX_RUN_SETUP,
            icon: <EyeOutlined />,
            label: <CompensationMatrixAnalysisLastConfigurationModal />,
          },
          {
            key: SmartFunctionActionDropdownKeys.REVIEW_ANALYSIS_CONFIGURATION,
            icon: <ControlOutlined />,
            label: <FlowCytometryReviewAnalysisConfigurationModal />,
          }
        ]
      }
      default:
        return []
    }
  }, [
    algorithmRun,
    selectedWorkspace,
    showFlagDeviationsMenuItem,
    deviations,
  ])

  const menuItems = useMemo(
    (): MenuProps['items'] => [
      ...(getCommonMenuItems() || []),
      ...(getWorkspaceTypeItems() || []),
    ],
    [getCommonMenuItems, getWorkspaceTypeItems]
  )

  return { getCommonMenuItems, getWorkspaceTypeItems, menuItems }
}

export default useSmartFunctionActionDropdownItems
